import { useEffect } from "react";
import { useLang } from "@/lib/i18n";

const PROD_BASE = "https://romannumeralsiii.com";

/**
 * SEO — lightweight head manager. No external dep (no react-helmet).
 * Sets document.title and injects/upserts:
 *   - meta description, robots
 *   - canonical link
 *   - Open Graph + Twitter Card tags
 *   - hreflang alternates (en/es)
 *   - per-page JSON-LD blocks (BreadcrumbList + optional Service/FAQPage)
 *
 * Props:
 *   title          string (required)
 *   description    string (required)
 *   path           string — site-relative path, e.g. "/services"
 *   image          string — absolute URL for OG image (defaults to logo)
 *   jsonLd         object | object[] — extra JSON-LD blocks to inject
 *   breadcrumbs    Array<{ name, path }> — auto-generates BreadcrumbList JSON-LD
 *   noindex        boolean — adds robots noindex,nofollow (for /admin /portal etc.)
 */
const DEFAULT_IMG = "https://customer-assets.emergentagent.com/job_8d4410f5-96b3-4ac7-ad5c-8a23a2562b5c/artifacts/xor9dgk5_ROMAN%20NUMERALS-LOGO-REVISED.png";

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, key, name] = selector.match(/meta\[(name|property)="([^"]+)"\]/) || [];
    if (key && name) el.setAttribute(key, name);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value ?? "");
};

const setLink = (rel, hrefValue, hreflang) => {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", hrefValue);
};

const SEO_SCRIPT_CLASS = "rn3-page-jsonld";
const replaceJsonLd = (blocks) => {
  // Remove previous page-specific JSON-LD
  document.head.querySelectorAll(`script.${SEO_SCRIPT_CLASS}`).forEach((s) => s.remove());
  const arr = Array.isArray(blocks) ? blocks : [blocks];
  arr.filter(Boolean).forEach((data) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.className = SEO_SCRIPT_CLASS;
    s.text = JSON.stringify(data);
    document.head.appendChild(s);
  });
};

const SEO = ({
  title,
  description,
  path = "/",
  image = DEFAULT_IMG,
  jsonLd = null,
  breadcrumbs = null,
  noindex = false,
}) => {
  const { lang } = useLang();

  useEffect(() => {
    const url = `${PROD_BASE}${path}`;
    const urlEs = path.includes("?") ? `${url}&lang=es` : `${url}?lang=es`;

    document.title = title;
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[name="robots"]', "content", noindex
      ? "noindex, nofollow"
      : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");

    setLink("canonical", url);
    setLink("alternate", url, "en");
    setLink("alternate", urlEs, "es");
    setLink("alternate", url, "x-default");

    // Open Graph
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:image"]', "content", image);
    setMeta('meta[property="og:type"]', "content", "website");
    setMeta('meta[property="og:locale"]', "content", lang === "es" ? "es_US" : "en_US");

    // Twitter
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", image);

    // Reflect language on <html>
    try { document.documentElement.lang = lang === "es" ? "es-US" : "en-US"; } catch { /* noop */ }

    // JSON-LD: auto-build BreadcrumbList if breadcrumbs provided
    const blocks = [];
    if (breadcrumbs && breadcrumbs.length) {
      blocks.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: `${PROD_BASE}${b.path}`,
        })),
      });
    }
    // WebPage wrapper
    blocks.push({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: title,
      description,
      inLanguage: lang === "es" ? "es-US" : "en-US",
      isPartOf: { "@id": `${PROD_BASE}/#website` },
      about: { "@id": `${PROD_BASE}/#organization` },
      primaryImageOfPage: { "@type": "ImageObject", url: image },
    });
    if (jsonLd) {
      const extra = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      extra.forEach((b) => blocks.push(b));
    }
    replaceJsonLd(blocks);
  }, [title, description, path, image, jsonLd, breadcrumbs, noindex, lang]);

  return null;
};

export default SEO;
